/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";

function DocumentSummaryPopup({ open, onClose, document, onViewDetails, t }) {
  const theme = useTheme();

  if (!document) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon
            sx={{ fontSize: 24, color: theme.palette.primary.main }}
          />
          <Typography variant="subtitle1" fontWeight="600">
            {t("documentSummaryPopup.title", "Work Permit Summary")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Permit Requested By Section */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
            >
              <PersonIcon
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing={0.3}
              >
                {t(
                  "documentSummaryPopup.requestedByLabel",
                  "Permit-Requested by"
                )}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="500" sx={{ pl: 3 }}>
              {document.author
                ? `${document.author.first_name} ${document.author.last_name}`
                : t("documentSummaryPopup.unknownAuthor", "Unknown")}
            </Typography>
          </Box>

          <Divider />

          {/* Work Type Section */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
            >
              <AccountTreeIcon
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing={0.3}
              >
                {t("documentSummaryPopup.workTypeLabel", "Work Type")}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="500" sx={{ pl: 3 }}>
              {document.workflow?.name ||
                t("documentSummaryPopup.unknownWorkType", "Unknown")}
            </Typography>
          </Box>

          <Divider />

          {/* Title Section */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
            >
              <DescriptionIcon
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing={0.3}
              >
                {t("documentSummaryPopup.titleLabel", "Title")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight="500"
              sx={{
                pl: 3,
                wordBreak: "break-word",
              }}
            >
              {document.title || t("documentSummaryPopup.noTitle", "No title")}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          p: 2,
          pt: 1.5,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("documentSummaryPopup.closeButton", "Close")}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onViewDetails(document.id);
            onClose();
          }}
          startIcon={<VisibilityIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            "&:hover": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          {t("documentSummaryPopup.viewDetailsButton", "View Full Details")}
        </Button>
      </Box>
    </Dialog>
  );
}

export default DocumentSummaryPopup;
